/**
 * Long moves on screen (ท่ายาว, design docs/design/combat/2026-10-11-long-moves.md §4.3): the client side of the rules'
 * long-move runner (packages/shared systems/longMove.ts). Everything is driven by the rules' state (`longMoveSysOf(w).view`:
 * the beat and seconds into the piece) and their events (`longTell` / `longStart` / `longBeat` / `longEnd`):
 * - poses: the caster's beat motion (motions/long `longCasterFrame`, the tell's `tellPose`, the whiff's `whiff.pose`) and the
 *   held foe's receive motion (`longFoeFrame`), drawn through the renderer's `received` (one hook line in `frameOf`). The
 *   caster's view is the heading held at the catch (toward the foe) plus the motion's own `turn` (mod 8); the held foe faces
 *   the caster as it stood at each beat's start;
 * - the ground mark of an ult's tell (passes/longMoveTell.ts reads `tellsOf`), kept to the end of the piece;
 * - per beat: its sound key (played when a sound by that id exists, silent otherwise), its camera cue (`flare`/`push` the
 *   cinematic, `shake` a kick; `pull`/`orbit` not yet), its hit-stop (`stop`, both bodies, per blow) and its afterimages
 *   (`ai` solid copies of the caster over the beat's start);
 * - an ported FX sample can be timed to the beats (`longRetime`, fx/longTempest.ts).
 * Wired from the effect registry (fx/longMoves.ts): it wraps the Effects' event handling like the ports do.
 */
import {
  artOfSkill,
  duelLockOpenOf,
  LONG_ULT,
  longMoveOf,
  longMoveSysOf,
  screenHeading,
  type Entity,
  type LongMoveDef,
  type LongMoveEvent,
  type World,
  type WorldEvent,
} from '@nine/shared';
import { hasSfx, playSfx } from '../sfx';
import type { FxHost } from './fx/registry';
import { hitStopHold } from './hitStop';
import { lastShotOf } from './lastShot';
import type { MotionId } from './motions';
import { longCasterFrame, longFoeFrame, type LongFrame } from './motions/long';

/** An ult's tell area, from `longTell` to the piece's end. */
export interface LongTellMark {
  src: number;
  skillId: string;
  shape: string;
  x: number;
  y: number;
  radius: number;
  range: number;
  dirX: number;
  dirY: number;
  /** World time the tell started and its length. */
  at: number;
  tell: number;
}

interface Run {
  def: LongMoveDef;
  /** Caster -> foe at the catch (world). */
  ux: number;
  uy: number;
  /** Held foe -> its direction toward the caster at the latest beat's start. */
  foeDir: Map<number, [number, number]>;
  beat: number;
  /** World times to leave an afterimage at. */
  ghosts: number[];
}

interface State {
  tells: Map<number, LongTellMark>;
  runs: Map<number, Run>;
  whiffs: Map<number, { def: LongMoveDef; at: number }>;
  /** The lift each body was last drawn with (afterimages sit where the body was drawn). */
  lift: Map<number, number>;
  /** Lock routines a caster runs (duelLockOpen's form), kept past the form's end at the `fall` beat for the last beats. */
  locks: Map<number, LockTail>;
  /** Each body's latest move (`skill` event) of a bare-handed art and when (its weapon stays in the scabbard). */
  bare: Map<number, number>;
}

interface LockTail {
  def: LongMoveDef;
  /** World time the routine started; the form's last seen time; the caster's swing then (a new swing ends the tail). */
  t0: number;
  last: number;
  swing: number;
  ux: number;
  uy: number;
}

const STATES = new WeakMap<object, State>();
function stateOf(w: object): State {
  let s = STATES.get(w);
  if (!s) STATES.set(w, (s = { tells: new Map(), runs: new Map(), whiffs: new Map(), lift: new Map(), locks: new Map(), bare: new Map() }));
  return s;
}

/** The ult tells to draw (their marks stay through the piece). */
export const tellsOf = (w: object): Iterable<LongTellMark> => stateOf(w).tells.values();

/** Cinematic strength of a long move's push: the renderer closes in by 0.3 × this (0.4 → ×1.12, design §1.1). */
export const LONG_CINE = 0.4;

const sound = (key: string | undefined): void => {
  if (key && hasSfx(key)) playSfx(key);
};

/** The heading a world direction has on screen now (via the camera's own `heading`). */
type HeadingOf = (v: { heading: number; dirX?: number; dirY?: number }) => number;
const headingOfDir = (headingOf: HeadingOf, ux: number, uy: number): number =>
  headingOf({ heading: screenHeading(ux, uy), dirX: ux, dirY: uy });

/** A long-move event (and every `hit`, for the beats' hit-stop), after the Effects' own handling. */
export function longMoveOnEvent(host: FxHost, ev: WorldEvent): void {
  const w = host.world;
  const S = stateOf(w);
  if (ev.type === 'skill') {
    if (artOfSkill(ev.skillId)?.weapon === 'unarmed') S.bare.set(ev.srcId, w.time);
    else S.bare.delete(ev.srcId);
    return;
  }
  if (ev.type === 'hit') {
    const run = S.runs.get(ev.srcId);
    const v = run && longMoveSysOf(w)?.view(ev.srcId);
    const stop = v && v.skillId === ev.skillId ? run.def.beats[v.beat]?.stop : undefined;
    if (stop && ev.outcome !== 'dodge') hitStopHold(ev.srcId, ev.tgtId, w.time, stop);
    return;
  }
  if (!ev.type.startsWith('long')) return;
  const e = ev as LongMoveEvent;
  const def = longMoveOf(e.skillId);
  if (!def) return;
  const src = w.get(e.src);
  switch (e.type) {
    case 'longTell':
      S.tells.set(e.src, { ...e, at: w.time });
      sound(def.opener.tellSfx);
      if (src) host.cinematic(src, LONG_CINE, e.tell, false); // `flare`: the dim, no close-in
      break;
    case 'longStart': {
      const foe = w.get(e.held[0] ?? e.struck[0] ?? -1);
      let ux = src ? (foe ? foe.x - src.x : src.dirX) : 1;
      let uy = src ? (foe ? foe.y - src.y : src.dirY) : 0;
      const l = Math.hypot(ux, uy) || 1;
      ux /= l;
      uy /= l;
      S.runs.set(e.src, { def, ux, uy, foeDir: new Map(), beat: -1, ghosts: [] });
      break;
    }
    case 'longBeat': {
      const run = S.runs.get(e.src);
      if (!run || !src) break;
      run.beat = e.i;
      for (const id of e.held) {
        const f = w.get(id);
        if (!f) continue;
        const l = Math.hypot(src.x - f.x, src.y - f.y) || 1;
        run.foeDir.set(id, [(src.x - f.x) / l, (src.y - f.y) / l]);
      }
      const b = def.beats[e.i];
      if (!b) break;
      sound(b.sfx);
      if (b.cam === 'push' || b.cam === 'flare') host.cinematic(src, LONG_CINE, 0.6, true);
      else if (b.cam === 'shake') host.shake = Math.max(host.shake, 2);
      for (let k = 0; k < (b.ai ?? 0); k++) run.ghosts.push(w.time + 0.03 + k * 0.05);
      break;
    }
    case 'longEnd':
      if (e.outcome === 'escaped') S.locks.delete(e.src);
      S.tells.delete(e.src);
      S.runs.delete(e.src);
      if (e.outcome === 'whiff') {
        S.whiffs.set(e.src, { def, at: w.time });
        sound(def.whiff?.sfx);
      }
      break;
  }
}

/** Every frame: the afterimages due, the old whiffs dropped. */
export function longMoveStep(host: FxHost): void {
  const w = host.world;
  const S = stateOf(w);
  for (const [id, run] of S.runs) {
    while (run.ghosts.length && run.ghosts[0]! <= w.time) {
      run.ghosts.shift();
      const e = w.get(id);
      const shot = lastShotOf(id, w.time);
      const set = e && host.sprites.of(e);
      if (!e || !shot || !set) continue;
      const [x, y] = host.pos(e);
      host.fx.push({
        type: 'ghost',
        step: true,
        x,
        y: y - e.z - (S.lift.get(id) ?? 0),
        t: 0,
        life: 0.4,
        img: shot.img,
        flip: shot.facing < 0,
        ax: set.ax,
        ay: set.ay,
      });
    }
  }
  for (const [id, wf] of S.whiffs) if (w.time - wf.at > (wf.def.whiff?.stop ?? 0.9) + 0.1) S.whiffs.delete(id);
}

/** The whiff under way for a caster (world time it began), or null. */
export const longWhiffAt = (w: object, id: number): number | null => stateOf(w).whiffs.get(id)?.at ?? null;

/**
 * What the renderer draws for a body in a long move this frame (null: not in one): a motion of the long group, how far
 * through it, the view turn relative to the body's own heading now, its roll and lift.
 */
export function longMoveFrame(
  w: World,
  e: Entity,
  headingOf: HeadingOf,
): { id: MotionId; u: number; turn: number; rot: number; lift: number } | null {
  const S = stateOf(w);
  const own = headingOf(e);
  const out = (f: LongFrame | null, abs?: number) => {
    if (!f) return null;
    S.lift.set(e.id, f.lift);
    const turn = abs === undefined ? f.turn : ((((abs + f.turn - own) % 8) + 8) % 8);
    return { id: f.id as MotionId, u: f.u, turn, rot: f.rot, lift: f.lift };
  };
  const v = longMoveSysOf(w)?.view(e.id);
  if (v) {
    const def = longMoveOf(v.skillId);
    const b = def?.beats[v.beat];
    if (!def || !b) return null;
    const k = (v.t - b.at) / Math.max(1e-6, (def.beats[v.beat + 1]?.at ?? def.len) - b.at);
    const run = S.runs.get(v.src);
    if (v.src === e.id) {
      const abs = run ? headingOfDir(headingOf, run.ux, run.uy) : undefined;
      return out(longCasterFrame(b.pose, k), abs);
    }
    const d = run?.foeDir.get(e.id);
    return out(longFoeFrame(def.beats, v.beat, k), d ? headingOfDir(headingOf, d[0], d[1]) : undefined);
  }
  const lk = lockCasterFrame(w, e, S);
  if (lk) return out(longCasterFrame(lk.pose, lk.k), headingOfDir(headingOf, lk.ux, lk.uy));
  // The tell: the cast of an ult (the World's wind-up), its own pose.
  const c = e.cast;
  const td = c ? longMoveOf(c.skillId) : null;
  if (c && td?.kind === 'ult' && td.opener.tellPose) return out(longCasterFrame(td.opener.tellPose, c.t / Math.max(1e-6, c.windup)));
  // The whiff's spent pose.
  const wf = S.whiffs.get(e.id);
  if (wf?.def.whiff) {
    const k = (w.time - wf.at) / wf.def.whiff.stop;
    if (k < 1) return out(longCasterFrame(wf.def.whiff.pose, k));
  }
  S.lift.delete(e.id);
  return null;
}

/**
 * Whether a body is drawn with its weapon in the scabbard for a bare-handed art's move (the art's `weapon: 'unarmed'`, e.g.
 * tongs-claw): its long move (tell, piece, lock routine and its tail, whiff), the cast of one of its moves, or one of its
 * moves just pressed (`skill` events). Plain attacks keep the weapon (no art on them). Read-only.
 */
export function bareHandedMove(w: World, e: Entity): boolean {
  const S = stateOf(w);
  const v = longMoveSysOf(w)?.view(e.id);
  const fv = duelLockOpenOf(w)?.formView(e.id);
  const id =
    (v && v.src === e.id ? v.skillId : null) ??
    (fv && fv.caster === e.id ? fv.skillId : null) ??
    e.cast?.skillId ??
    S.locks.get(e.id)?.def.skillId ??
    S.whiffs.get(e.id)?.def.skillId ??
    null;
  if (id) return artOfSkill(id)?.weapon === 'unarmed';
  // A plain move of the art (the lunge of a lock form, a strike): while it swings, or just after it was pressed.
  const at = S.bare.get(e.id);
  if (at === undefined) return false;
  if (w.time - at < 0.6 || (e.swingT > 0 && w.time - at < 2)) return true;
  S.bare.delete(e.id);
  return false;
}

/** The beat at piece time `t` and how far through it (as data/lockRoutines' `beatAt`). */
function beatOf(def: LongMoveDef, t: number): { i: number; k: number } {
  let i = 0;
  while (i + 1 < def.beats.length && t >= def.beats[i + 1]!.at) i++;
  const from = def.beats[i]!.at;
  return { i, k: (t - from) / Math.max(1e-6, (def.beats[i + 1]?.at ?? def.len) - from) };
}

/**
 * A lock routine's caster (ท่ายาว lock form; read-only on the rules): the routine's beat from duelLockOpen's `formView` (the
 * long-move view covers the ults only). The form ends at the routine's `fall` beat (the foe goes to the knock-down rules), so
 * the caster's last beats (fall, recover) play on here by the routine's own clock until its length, unless the caster acts
 * (a cast, a new swing, a step, a dash) or dies. An escape ends the form before the fall: no tail then.
 */
function lockCasterFrame(
  w: World,
  e: Entity,
  S: State,
): { pose: string; k: number; ux: number; uy: number } | null {
  const fv = duelLockOpenOf(w)?.formView(e.id);
  const def = fv && fv.caster === e.id ? longMoveOf(fv.skillId) : null;
  if (fv && def?.kind === 'lock') {
    const prev = S.locks.get(e.id);
    const run = S.runs.get(e.id);
    const same = prev && prev.def === def && prev.last <= fv.t;
    const ux = run?.ux ?? (same ? prev.ux : e.dirX);
    const uy = run?.uy ?? (same ? prev.uy : e.dirY);
    S.locks.set(e.id, { def, t0: w.time - fv.t, last: fv.t, swing: e.swingT, ux, uy });
    const b = def.beats[fv.beat];
    return b ? { pose: b.pose, k: fv.k, ux, uy } : null;
  }
  const tl = S.locks.get(e.id);
  if (!tl) return null;
  const t = w.time - tl.t0;
  const fall = tl.def.beats.find((b) => b.foe === 'fall');
  const acted = !!e.cast || e.dead || e.moving || e.dashT > 0 || e.swingT > tl.swing + 1e-6;
  // The form ended at the fall (the last frame drawn may be a few steps before it); an escape ends it at the catch.
  if (!fall || tl.last < fall.at - 0.25 || t >= tl.def.len || acted) {
    S.locks.delete(e.id);
    return null;
  }
  tl.swing = e.swingT;
  const b = beatOf(tl.def, t);
  const beat = tl.def.beats[b.i];
  return beat ? { pose: beat.pose, k: b.k, ux: tl.ux, uy: tl.uy } : null;
}

/**
 * A ported FX sample timed to a long move's beats: sample time from the piece's own clock (the tell's seconds are
 * negative) through `anchors` ([piece s, sample s] pairs, ascending), linear between. `end` once the move whiffed; null
 * when the caster is in neither (the port keeps its own clock).
 */
export function longRetime(
  w: World,
  skillId: string,
  anchors: readonly (readonly [number, number])[],
  end: number,
): (srcId: number) => number | null {
  const map = (t: number): number => {
    if (t <= anchors[0]![0]) return anchors[0]![1];
    for (let i = 1; i < anchors.length; i++) {
      const [t0, s0] = anchors[i - 1]!;
      const [t1, s1] = anchors[i]!;
      if (t <= t1) return s0 + ((s1 - s0) * (t - t0)) / Math.max(1e-6, t1 - t0);
    }
    const [tl, sl] = anchors[anchors.length - 1]!;
    return sl + (t - tl);
  };
  return (srcId) => {
    const v = longMoveSysOf(w)?.view(srcId);
    if (v && v.src === srcId && v.skillId === skillId) return map(v.t);
    const c = w.get(srcId)?.cast;
    if (c && c.skillId === skillId) return map(c.t - c.windup);
    if (stateOf(w).whiffs.has(srcId)) return end;
    return null;
  };
}

/** The piece time of each blow of a beat (the rules' spread over the beat's start). */
export function blowTimes(def: LongMoveDef, i: number): number[] {
  const b = def.beats[i]!;
  const to = def.beats[i + 1]?.at ?? def.len;
  return Array.from({ length: b.hits }, (_, j) => b.at + ((j + 0.5) / b.hits) * LONG_ULT.blowSpread * (to - b.at));
}
